<template>
  <v-dialog
    :value="show"
    width="640"
    color="white black--text"
    persistent
    no-click-animation
    @keydown.esc="cancel"
    scrollable
    class="dialog-record-detail"
  >
    <WithEntity
      v-slot="{ entity, loading: loadingEntity, headers }"
      :Model="Model"
      :id="id"
      view-type="detail"
    >
      <v-card v-if="loading" key="loading-table-data">
        <v-card-title class="py-3">
          <h2 class="text-h6 grey--text font-italic mx-auto"
            >Loading {{ Model.nameSingular }}</h2
          >
        </v-card-title>
        <v-card-text class="pt-2 pb-8">
          <v-row class="edit-detail-container">
            <v-col cols="12 text-center">
              <v-progress-circular
                indeterminate
                class="mt-2"
                size="20"
                width="2"
              />
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <v-card v-else key="loaded-record-detail">
        <v-card-title class="py-3 justify-space-between">
          <h2 class="text-h6 grey--text font-italic">
            {{
              isNewEntity(entity)
                ? `New ${Model.nameSingular}`
                : `Edit ${Model.nameSingular}`
            }}
          </h2>

          <template
            v-if="
              Model.namePlural === AllModels.OrderModel.namePlural &&
                !isNewEntity(entity) &&
                !!entity &&
                entity.hasOwnProperty('id')
            "
          >
            <v-spacer />
            <v-btn
              :to="{ name: PrintOrderView, params: { id: entity.id } }"
              small
              outlined
              color="blue darken-1"
              ><v-icon left small>mdi-open-in-new</v-icon> Print</v-btn
            >
          </template>
        </v-card-title>

        <v-card-text class="pt-2 pb-8">
          <v-row class="edit-detail-container">
            <v-col cols="12">
              <AdminEditItemForm
                v-if="Model.namePlural === AllModels.ItemModel.namePlural"
                :entity="entity"
                @form:valid="isFormValid = $event"
                @update="$emit('update', $event)"
                @hook:created="original = entity"
              />
              <template
                v-else-if="Model.namePlural === AllModels.OrderModel.namePlural"
              >
                <WithCart
                  v-if="isNewEntity(entity)"
                  v-slot="{ cart, loading }"
                  :is-custom="true"
                >
                  <v-row v-if="loading" class="pa-6 text-center" dense>
                    <v-col cols="12">
                      <h4>Loading...</h4>
                    </v-col>
                    <v-col cols="12">
                      <v-progress-circular indeterminate></v-progress-circular>
                    </v-col>
                  </v-row>
                  <AdminNewOrderForm
                    v-else
                    :cart="cart"
                    @form:valid="isFormValid = $event"
                    @update="$emit('update-cart', { id: cart.id, ...$event })"
                    @hook:created="original = entity"
                  />
                </WithCart>
                <template v-else>
                  <transition name="fade-fast" mode="out-in">
                    <div
                      v-if="loadingEntity"
                      class="text-center"
                      key="loading-order"
                    >
                      <h3 class="mt-2">Loading Order</h3>
                      <v-progress-circular
                        indeterminate
                        class="mt-2"
                        size="20"
                        width="2"
                      />
                    </div>
                    <AdminEditOrderForm
                      v-else
                      :entity="entity"
                      @form:valid="isFormValid = $event"
                      @update="$emit('update', $event)"
                      @hook:created="original = entity"
                    />
                  </transition>
                </template>
              </template>

              <AdminEditCustomerForm
                v-else-if="
                  Model.namePlural === AllModels.CustomerModel.namePlural
                "
                :entity="entity"
                @form:valid="isFormValid = $event"
                @update="$emit('update', $event)"
                @hook:created="original = entity"
              />
              <AdminEditAvailabilityForm
                v-else-if="
                  Model.namePlural === AllModels.AvailabilityModel.namePlural
                "
                :entity="entity"
                @form:valid="isFormValid = $event"
                @update="$emit('update', $event)"
                @hook:created="original = entity"
              />
              <AdminEditRecordForm
                v-else
                :entity="entity"
                :loading="loadingEntity"
                :headers="headers"
                :Model="Model"
                @form:valid="isFormValid = $event"
                @update="$emit('update', $event)"
                @hook:created="original = entity"
              />
            </v-col>
          </v-row>
        </v-card-text>

        <v-alert v-if="Object.keys(errors).length" type="error" text>
          <div
            v-for="{ title, errors } of parseErrors(headers)"
            :key="JSON.stringify(errors)"
            >{{ title }} : {{ errors }}</div
          >
        </v-alert>
        <v-alert v-if="deleteError" type="error" text>
          <div>{{ deleteError }}</div>
        </v-alert>
        <v-alert v-if="cloneError" type="error" text>
          <div>{{ cloneError }}</div>
        </v-alert>
        <v-card-actions class="pt-4">
          <BtnDeleteConfirmation
            v-if="canModelDelete && !isNewEntity(entity)"
            @delete="deleteRecord"
            :loading="isDeleting"
            tooltip="Archive this Record?"
            tooltip-position="right"
          />
          <v-btn
            v-if="canModelClone && !isNewEntity(entity)"
            outlined
            small
            @click="cloneEntity"
            :loading="isCloning"
            title="Duplicate Record"
            aria-label="Duplicate Record"
            color="blue"
            class="ml-2"
          >
            <v-icon small left class="ml-0"> mdi-content-copy </v-icon>Duplicate
          </v-btn>

          <v-spacer></v-spacer>

          <v-btn
            color="blue darken-2 white--text"
            depressed
            :disabled="!isFormValid || saving"
            :loading="saving"
            @click="saveEntity(entity)"
          >
            {{ isNewEntity(entity) ? `Add ${Model.nameSingular}` : 'Save' }}
          </v-btn>

          <v-btn
            color="deep-orange"
            outlined
            @click="cancel"
            :disabled="saving"
          >
            Cancel
          </v-btn>
        </v-card-actions>
      </v-card>
    </WithEntity>
  </v-dialog>
</template>

<script>
import { Model } from '@vuex-orm/core'
import AllModels from '@/models'
import { mapFields } from 'vuex-map-fields'
import RouteNames from '@/constants/RouteNames'

import WithCart from '@/components/DataProviders/WithCart'
import WithEntity from '@/components/DataProviders/WithEntity'
import AdminEditItemForm from '@/components/AdminEditItemForm'
import AdminEditOrderForm from '@/components/AdminEditOrderForm'
import AdminNewOrderForm from '@/components/AdminNewOrderForm'
import AdminEditCustomerForm from '@/components/AdminEditCustomerForm'
import AdminEditAvailabilityForm from '@/components/AdminEditAvailabilityForm'
import AdminEditRecordForm from '@/components/AdminEditRecordForm'

import BtnDeleteConfirmation from '@/components/BtnDeleteConfirmation'

import _get from 'lodash/get'
export default {
  name: 'DialogRecordDetail',
  components: {
    AdminEditRecordForm,
    AdminEditItemForm,
    AdminEditOrderForm,
    AdminNewOrderForm,
    AdminEditCustomerForm,
    AdminEditAvailabilityForm,
    WithEntity,
    WithCart,
    BtnDeleteConfirmation,
  },
  props: {
    Model: {
      type: Model,
      default: () => Model,
    },
    id: {
      type: [String, Number],
      require: true,
      default: null,
    },
    show: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    saving: {
      type: Boolean,
      default: false,
    },
    errors: {
      type: Object,
      default: () => {},
    },
  },
  meta() {
    return { title: `Edit ${this.Model.nameSingular} | Minimart` }
  },
  data() {
    return {
      isFormValid: false,
      AllModels,
      original: null,
      isDeleting: false,
      deleteError: false,
      isCloning: false,
      cloneError: false,
      PrintOrderView: RouteNames.Merchant.Manage.PRINT_ORDER,
    }
  },

  methods: {
    cancel() {
      if (this.original.$isNew) {
        this.$emit('delete')
      } else {
        this.$emit('update', this.original)
      }
      this.$emit('close')
    }, // cancel
    parseErrors(headers) {
      return Object.keys(this.errors).map((fieldName) => {
        let header = headers.find((h) => h.value === fieldName)
        let title = header ? header.text : fieldName
        return { title, errors: this.errors[fieldName].join(',') }
      })
    }, // parseErrors
    async deleteRecord() {
      this.isDeleting = true
      try {
        await this.Model.deleteModel(this.id)
        this.showRecordDeletedSnackbar = true
        this.$emit('close')
        this.isTableStale = true
      } catch (error) {
        this.deleteError = error
      } finally {
        this.isDeleting = false
      }
    }, // deleteRecord

    async saveEntity(entity) {
      this.isNewEntity(entity) && this.Model === AllModels.OrderModel
        ? await this.$emit('create-order')
        : await this.$emit('save')
    }, // saveEntity

    async cloneEntity() {
      if (this.canModelClone) {
        this.isCloning = true
        try {
          await this.Model.clone(this.id)
          this.showRecordClonedSnackbar = true
          this.$emit('close')
          this.isTableStale = true
        } catch (error) {
          this.cloneError = error
        } finally {
          this.isCloning = false
        }
      }
    }, // cloneEntity
    isNewEntity(entity) {
      return _get(entity, '$isNew', false)
    },
  }, // methods
  computed: {
    ...mapFields('snackbars', [
      'showRecordDeletedSnackbar',
      'showRecordClonedSnackbar',
    ]),
    ...mapFields('admin', ['isTableStale']),
    canModelDelete() {
      let modelTableConfig = this.Model.index_table_config
        ? this.Model.index_table_config()
        : {}

      return _get(modelTableConfig, 'removeable', true)
    }, // canModelDelete
    canModelClone() {
      return 'function' === typeof this.Model.clone
    }, // canModelClone
  },
}
</script>

<style>
.edit-detail-container {
  max-width: 600px;
}
.dialog-record-detail {
  position: relative;
}
</style>
